import React, { useState } from 'react';

interface MatchCardProps {
  match: any;
  puuid: string;
  ddragonVersion: string;
}

const QUEUE_NAMES: Record<number, string> = {
  420: 'Ranked Solo',
  440: 'Ranked Flex',
  400: 'Normal Draft',
  430: 'Normal Blind',
  450: 'ARAM',
  490: 'Quickplay',
  900: 'URF',
  1700: 'Arena',
};

const SUMMONER_SPELLS: Record<number, string> = {
  1: 'SummonerBoost',
  3: 'SummonerExhaust',
  4: 'SummonerFlash',
  6: 'SummonerHaste',
  7: 'SummonerHeal',
  11: 'SummonerSmite',
  12: 'SummonerTeleport',
  13: 'SummonerMana',
  14: 'SummonerDot',
  21: 'SummonerBarrier',
  30: 'SummonerPoroRecall',
  31: 'SummonerPoroThrow',
  32: 'SummonerSnowball',
  39: 'SummonerSnowURFSnowball_Mark',
  54: 'Summoner_UltBookPlaceholder',
  55: 'Summoner_UltBookSmitePlaceholder',
};

function timeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatNumber(n: number): string {
  if (n >= 1000) return (n / 1000).toFixed(1) + 'k';
  return n.toString();
}

const MatchCard: React.FC<MatchCardProps> = ({ match, puuid, ddragonVersion }) => {
  const [expanded, setExpanded] = useState(false);
  const participant = match.info.participants.find((p: any) => p.puuid === puuid);
  if (!participant) return null;

  const isWin = participant.win;
  const queueName = QUEUE_NAMES[match.info.queueId] || 'Game';
  const kills = participant.kills;
  const deaths = participant.deaths;
  const assists = participant.assists;
  const kda = deaths === 0 ? 'Perfect' : ((kills + assists) / deaths).toFixed(1);
  const cs = participant.totalMinionsKilled + participant.neutralMinionsKilled;
  const csPerMin = (cs / (match.info.gameDuration / 60)).toFixed(1);
  const championName = participant.championName;
  const champLevel = participant.champLevel;

  const championIconUrl = `https://ddragon.leagueoflegends.com/cdn/${ddragonVersion}/img/champion/${championName}.png`;

  const spell1 = SUMMONER_SPELLS[participant.summoner1Id];
  const spell2 = SUMMONER_SPELLS[participant.summoner2Id];
  const spell1Url = spell1
    ? `https://ddragon.leagueoflegends.com/cdn/${ddragonVersion}/img/spell/${spell1}.png`
    : undefined;
  const spell2Url = spell2
    ? `https://ddragon.leagueoflegends.com/cdn/${ddragonVersion}/img/spell/${spell2}.png`
    : undefined;

  const trinket = participant.item6;
  const roleBoundItem = participant.roleBoundItem || 0;

  const row1 = [participant.item0, participant.item1, participant.item2, trinket];
  const row2 = [participant.item3, participant.item4, participant.item5, roleBoundItem];

  const team1 = match.info.participants.filter((p: any) => p.teamId === 100);
  const team2 = match.info.participants.filter((p: any) => p.teamId === 200);

  const winningTeamId = match.info.participants.find((p: any) => p.win)?.teamId || 100;
  const winningTeam = winningTeamId === 100 ? team1 : team2;
  const losingTeam = winningTeamId === 100 ? team2 : team1;

  const renderItem = (itemId: number, key: string) => (
    <div key={key} className="item-slot">
      {itemId > 0 ? (
        <img
          src={`https://ddragon.leagueoflegends.com/cdn/${ddragonVersion}/img/item/${itemId}.png`}
          alt={`Item ${itemId}`}
          className="item-icon"
        />
      ) : (
        <div className="item-empty" />
      )}
    </div>
  );

  const renderSmallItem = (itemId: number, key: string) => (
    <div key={key} className="item-slot-sm">
      {itemId > 0 ? (
        <img
          src={`https://ddragon.leagueoflegends.com/cdn/${ddragonVersion}/img/item/${itemId}.png`}
          alt={`Item ${itemId}`}
          className="item-icon-sm"
        />
      ) : (
        <div className="item-empty-sm" />
      )}
    </div>
  );

  const renderScoreRow = (p: any) => {
    const pKills = p.kills;
    const pDeaths = p.deaths;
    const pAssists = p.assists;
    const pKda = pDeaths === 0 ? 'Perfect' : ((pKills + pAssists) / pDeaths).toFixed(2);
    const pCs = p.totalMinionsKilled + p.neutralMinionsKilled;
    const pDamage = p.totalDamageDealtToChampions;
    const pGold = p.goldEarned;
    const pWards = p.wardsPlaced;
    const pItems = [p.item0, p.item1, p.item2, p.item3, p.item4, p.item5, p.item6];
    const pRoleBound = p.roleBoundItem || 0;
    const isSelf = p.puuid === puuid;

    return (
      <div key={p.puuid} className={`score-row ${isSelf ? 'score-row-self' : ''}`}>
        <div className="score-champ">
          <img
            src={`https://ddragon.leagueoflegends.com/cdn/${ddragonVersion}/img/champion/${p.championName}.png`}
            alt={p.championName}
            className="score-champ-icon"
          />
          <span className="score-champ-level">{p.champLevel}</span>
        </div>
        <div className="score-name">
          {p.riotIdGameName || p.summonerName}
        </div>
        <div className="score-kda">
          <span className="kda-kills">{pKills}</span>
          <span className="kda-slash">/</span>
          <span className="kda-deaths">{pDeaths}</span>
          <span className="kda-slash">/</span>
          <span className="kda-assists">{pAssists}</span>
          <div className={`score-kda-ratio ${pKda === 'Perfect' ? 'kda-perfect' : ''}`}>
            {pKda === 'Perfect' ? 'Perfect' : `${pKda} KDA`}
          </div>
        </div>
        <div className="score-damage">{formatNumber(pDamage)}</div>
        <div className="score-gold">{formatNumber(pGold)}</div>
        <div className="score-cs">{pCs}</div>
        <div className="score-wards">{pWards}</div>
        <div className="score-items">
          {pItems.map((id: number, i: number) => renderSmallItem(id, `si-${p.puuid}-${i}`))}
          {pRoleBound > 0 && renderSmallItem(pRoleBound, `si-${p.puuid}-rb`)}
        </div>
      </div>
    );
  };

  return (
    <div className={`match-card-wrapper ${isWin ? 'match-win' : 'match-loss'}`}>
      <div className="match-card" onClick={() => setExpanded(!expanded)}>
        <div className={`match-result-bar ${isWin ? 'bar-win' : 'bar-loss'}`} />

        <div className="match-info-col">
          <span className="match-queue">{queueName}</span>
          <span className="match-time-ago">{timeAgo(match.info.gameCreation)}</span>
          <span className={`match-result-text ${isWin ? 'text-win' : 'text-loss'}`}>
            {isWin ? 'Victory' : 'Defeat'}
          </span>
          <span className="match-duration">{formatDuration(match.info.gameDuration)}</span>
        </div>

        <div className="match-champion-col">
          <div className="champion-icon-wrapper">
            <img src={championIconUrl} alt={championName} className="match-champion-icon" />
            <span className="match-champ-level">{champLevel}</span>
          </div>
          <div className="match-spells">
            {spell1Url && <img src={spell1Url} alt="Spell 1" className="spell-icon" />}
            {spell2Url && <img src={spell2Url} alt="Spell 2" className="spell-icon" />}
          </div>
        </div>

        <div className="match-kda-col">
          <div className="kda-numbers">
            <span className="kda-kills">{kills}</span>
            <span className="kda-slash"> / </span>
            <span className="kda-deaths">{deaths}</span>
            <span className="kda-slash"> / </span>
            <span className="kda-assists">{assists}</span>
          </div>
          <div className="kda-ratio">
            {kda === 'Perfect' ? (
              <span className="kda-perfect">Perfect KDA</span>
            ) : (
              <span>{kda} KDA</span>
            )}
          </div>
          <span className="match-cs">{cs} CS ({csPerMin}/min)</span>
          <span className="match-vision">Vision {participant.visionScore}</span>
        </div>

        <div className="match-items-col">
          <div className="items-row">
            {row1.map((id, i) => renderItem(id, `r1-${i}`))}
          </div>
          <div className="items-row">
            {row2.map((id, i) => renderItem(id, `r2-${i}`))}
          </div>
        </div>

        <div className="match-players-col">
          <div className="players-team">
            {team1.map((p: any) => (
              <div key={p.puuid} className={`player-row ${p.puuid === puuid ? 'player-self' : ''}`}>
                <img
                  src={`https://ddragon.leagueoflegends.com/cdn/${ddragonVersion}/img/champion/${p.championName}.png`}
                  alt={p.championName}
                  className="player-champ-icon"
                />
                <span className="player-name">{p.riotIdGameName || p.summonerName}</span>
              </div>
            ))}
          </div>
          <div className="players-team">
            {team2.map((p: any) => (
              <div key={p.puuid} className={`player-row ${p.puuid === puuid ? 'player-self' : ''}`}>
                <img
                  src={`https://ddragon.leagueoflegends.com/cdn/${ddragonVersion}/img/champion/${p.championName}.png`}
                  alt={p.championName}
                  className="player-champ-icon"
                />
                <span className="player-name">{p.riotIdGameName || p.summonerName}</span>
              </div>
            ))}
          </div>
        </div>

        <div className={`expand-arrow ${expanded ? 'arrow-up' : ''}`}>&#9662;</div>
      </div>

      {expanded && (
        <div className="match-expanded">
          <div className="score-header">
            <div className="score-header-label">KDA</div>
            <div className="score-header-label">Damage</div>
            <div className="score-header-label">Gold</div>
            <div className="score-header-label">CS</div>
            <div className="score-header-label">Wards</div>
            <div className="score-header-label">Items</div>
          </div>

          <div className="score-team-header team-victory">Victory ({winningTeamId === 100 ? 'Blue' : 'Red'} Team)</div>
          {winningTeam.map(renderScoreRow)}

          <div className="score-team-header team-defeat">Defeat ({winningTeamId === 100 ? 'Red' : 'Blue'} Team)</div>
          {losingTeam.map(renderScoreRow)}
        </div>
      )}
    </div>
  );
};

export default MatchCard;
